import { useMemo, useState } from "react";
import { Copy } from "lucide-react";
import { Button } from "@/components/system";
import { track } from "@/lib/analytics";
import {
  buildReferralTemplate,
  type ReferralTemplateInput,
} from "@/lib/referral-template";

export function ReferralComposer({
  input,
  facilityId,
  headingId = "referral-composer-heading",
}: {
  input: ReferralTemplateInput;
  facilityId: string;
  headingId?: string;
}) {
  const [copied, setCopied] = useState(false);
  const template = useMemo(() => buildReferralTemplate(input), [input]);

  return (
    <section className="ds-stack" style={{ gap: 14 }} aria-labelledby={headingId}>
      <div className="ds-stack" style={{ gap: 6 }}>
        <h2 id={headingId} className="ds-title">Referral letter template</h2>
        <p className="ds-small">Fill the square-bracket placeholders in your clinical system. Nothing is entered or sent here.</p>
      </div>
      <textarea className="ds-textarea" style={{ minHeight: 320 }} readOnly value={template} aria-label="Referral letter template" />
      <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
        <p className="ds-fine">No patient information is collected by PulseBoard.</p>
        <Button
          onClick={() => {
            void copyText(template).then((didCopy) => {
              setCopied(didCopy);
              if (didCopy) {
                track("referral_template_copied", { facility_id: facilityId });
                window.setTimeout(() => setCopied(false), 2500);
              }
            });
          }}
        >
          <Copy aria-hidden="true" />
          {copied ? "Template copied" : "Copy template"}
        </Button>
      </div>
    </section>
  );
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  }
}
